import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ArrowLeft, ChevronRight, Filter } from 'Components/DesignSystem/Iconography/Icons';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Search from 'Components/DesignSystem/Search/Search';
import MenuButton from './MenuButton';
import { CATEGORY, CATEGORY_TYPE, CO_SOLD_CATEGORY, CO_SOLD_CATEGORY_TYPE, CURATED } from '../constants/CategoryRelevancyConstants';

type FilterButtonProps = {
    filters: FilterOptions;
    onFilterChange: (filters: FilterOptions) => void;
    categories: Category[];
    categoryTypes: CategoryType[];
    isOpen: boolean;
    onToggle: () => void;
    activeTab: TabType;
}

type FilterTab = keyof FilterOptions | null;

/* The DS popover chrome (§ 217 grammar — the same surface Menu bakes). */
const POPOVER_CHROME = 'tw-absolute tw-top-full tw-right-0 tw-mt-1 tw-w-52 tw-bg-surface-raised tw-rounded-srf tw-border tw-border-edge tw-shadow-ds-md tw-inline-flex tw-flex-col tw-justify-start tw-items-start tw-overflow-hidden';

const FilterButton = ({ filters, onFilterChange, categories, categoryTypes, isOpen, onToggle, activeTab }: FilterButtonProps) => {
    const [activeFilter, setActiveFilter] = useState<FilterTab>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const menuRef = useRef<HTMLDivElement>(null);

    const handleFilterClick = (filterType: FilterTab) => {
        setActiveFilter(filterType === activeFilter ? null : filterType);
        setSearchTerm('');
    };

    const handleMenuButtonClick = () => {
        if (activeFilter) {
            setActiveFilter(null);
        } else {
            onToggle();
        }
    };

    const handleBackButtonClick = () => {
        setActiveFilter(null);
    };

    const isItemSelected = <T extends Category | CategoryType>(
        item: T,
        currentItems: T[],
        idField: keyof T
    ): boolean => {
        return currentItems.some(existingItem => existingItem[idField] === item[idField]);
    };

    const handleItemToggle = <T extends Category | CategoryType>(
        item: T,
        currentItems: T[],
        updateFn: (newItems: T[]) => void,
        idField: keyof T
    ) => {
        if (isItemSelected(item, currentItems, idField)) {
            const newItems = currentItems.filter(existingItem =>
                existingItem[idField] !== item[idField]
            );
            updateFn(newItems);
        } else {
            updateFn([...currentItems, item]);
        }
    };

    const handleCategorySelect = (category: Category) => {
        handleItemToggle(
            category,
            filters.categories,
            (newCategories) => onFilterChange({ ...filters, categories: newCategories }),
            'category_id'
        );
    };

    const handleCategoryTypeSelect = (categoryType: CategoryType) => {
        handleItemToggle(
            categoryType,
            filters.category_types,
            (newCategoryTypes) => onFilterChange({ ...filters, category_types: newCategoryTypes }),
            'category_type_id'
        );
    };

    const handleCoSoldCategorySelect = (category: Category) => {
        handleItemToggle(
            category,
            filters.co_sold_categories,
            (newCategories) => onFilterChange({ ...filters, co_sold_categories: newCategories }),
            'category_id'
        );
    };

    const handleCoSoldCategoryTypeSelect = (categoryType: CategoryType) => {
        handleItemToggle(
            categoryType,
            filters.co_sold_category_types,
            (newCategoryTypes) => onFilterChange({ ...filters, co_sold_category_types: newCategoryTypes }),
            'category_type_id'
        );
    };

    const handleCuratedCheckboxToggle = (checkboxValue: 'active' | 'inactive') => {
        const currentCheckboxState = filters.curatedSelections || [];

        let newCheckboxState: ('active' | 'inactive')[];

        if (currentCheckboxState.includes(checkboxValue)) {
            newCheckboxState = currentCheckboxState.filter(v => v !== checkboxValue);
        } else {
            newCheckboxState = [...currentCheckboxState, checkboxValue];
        }

        const determineApiCuratedValue = (checkboxState: ('active' | 'inactive')[]) => {
            if (checkboxState.includes('active') && !checkboxState.includes('inactive')) {
                return true;
            }

            if (checkboxState.includes('inactive') && !checkboxState.includes('active')) {
                return false;
            }

            return undefined;
        };

        onFilterChange({
            ...filters,
            curatedSelections: newCheckboxState,
            is_curated: determineApiCuratedValue(newCheckboxState)
        });
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node) && activeFilter) {
                setActiveFilter(null);
            }
        };

        if (activeFilter) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeFilter]);

    const filteredCategories = useMemo(() => {
        if (!searchTerm) return categories;
        const searchLower = searchTerm.toLowerCase();
        return categories.filter(cat =>
            cat.category_name.toLowerCase().includes(searchLower)
        );
    }, [categories, searchTerm]);

    const filteredCategoryTypes = useMemo(() => {
        if (!searchTerm) return categoryTypes;
        const searchLower = searchTerm.toLowerCase();
        return categoryTypes.filter(type =>
            type.category_type_name.toLowerCase().includes(searchLower)
        );
    }, [categoryTypes, searchTerm]);

    const baseFilterOptions = [
        { key: 'categories', label: CATEGORY },
        { key: 'category_types', label: CATEGORY_TYPE },
        { key: 'co_sold_categories', label: CO_SOLD_CATEGORY },
        { key: 'co_sold_category_types', label: CO_SOLD_CATEGORY_TYPE },
    ];
    const filterOptions = activeTab === 'all'
        ? [...baseFilterOptions, { key: 'is_curated', label: CURATED }]
        : baseFilterOptions;

    const renderBackButton = (label: string) => (
        <button data-testid="components-filterbutton-button-1"
            className="tw-flex tw-items-center tw-text-ink tw-text-sm tw-leading-tight"
            onClick={handleBackButtonClick}
            aria-label={`Back to filter menu from ${label}`}
        >
            <div className="tw-mr-2 tw-text-ink-secondary"><ArrowLeft /></div>
            <span>{label}</span>
        </button>
    );

    const renderSearchInput = (placeholder: string) => (
        <Search
            size="sm"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(val) => setSearchTerm(val)}
            data-testid="components-filterbutton-input-2"
        />
    );

    return (
        <div className="tw-relative" ref={menuRef}>
            <MenuButton
                isOpen={isOpen}
                onToggle={handleMenuButtonClick}
                icon={<Filter />}
                label="Filter"
            />

            {isOpen && !activeFilter && (
                <div className={`${POPOVER_CHROME} tw-z-10`}>
                    {filterOptions.map((option, index) => (
                        <button data-testid={`components-filterbutton-button-3-${index}`}
                            key={option.key}
                            className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 hover:tw-bg-surface-hover tw-flex tw-justify-between tw-items-center tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-w-full"
                            onClick={() => handleFilterClick(option.key as FilterTab)}
                        >
                            <div className="tw-text-left tw-text-ink tw-text-sm tw-leading-tight">
                                {option.label}
                            </div>
                            <div className="tw-w-6 tw-h-6 tw-flex tw-justify-center tw-items-center tw-text-ink-tertiary">
                                <ChevronRight size={16} />
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {activeFilter === 'categories' && (
                <div className={`${POPOVER_CHROME} tw-z-20`}>
                    <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-justify-start tw-items-center">
                        {renderBackButton('Category')}
                    </div>
                    <div className="tw-self-stretch tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge">
                        {renderSearchInput('Search Category')}
                    </div>
                    <div className="tw-self-stretch tw-max-h-64 tw-overflow-y-auto">
                        {filteredCategories.map((item) => (
                            <div
                                key={`categories-${item.category_id}`}
                                className="tw-self-stretch tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-w-full"
                            >
                                <Checkbox
                                    size="sm"
                                    id={`item-${item.category_id}`}
                                    data-testid="components-filterbutton-input-4"
                                    checked={isItemSelected(item, filters.categories, 'category_id')}
                                    onChange={() => handleCategorySelect(item)}
                                    label={item.category_name}
                                />
                            </div>
                        ))}
                        {filteredCategories.length === 0 && (
                            <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-justify-center tw-w-full">
                                <span className="tw-text-ink-secondary tw-text-sm tw-leading-tight">No results found</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeFilter === 'category_types' && (
                <div className={`${POPOVER_CHROME} tw-z-20`}>
                    <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-justify-start tw-items-center">
                        {renderBackButton('Category Type')}
                    </div>
                    <div className="tw-self-stretch tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge">
                        {renderSearchInput('Search Category Type')}
                    </div>
                    <div className="tw-self-stretch tw-max-h-64 tw-overflow-y-auto">
                        {filteredCategoryTypes.map((item) => (
                            <div
                                key={`categoryTypes-${item.category_type_id}`}
                                className="tw-self-stretch tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-w-full"
                            >
                                <Checkbox
                                    size="sm"
                                    id={`item-${item.category_type_id}`}
                                    data-testid="components-filterbutton-input-5"
                                    checked={isItemSelected(item, filters.category_types, 'category_type_id')}
                                    onChange={() => handleCategoryTypeSelect(item)}
                                    label={item.category_type_name}
                                />
                            </div>
                        ))}
                        {filteredCategoryTypes.length === 0 && (
                            <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-justify-center tw-w-full">
                                <span className="tw-text-ink-secondary tw-text-sm tw-leading-tight">No results found</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeFilter === 'co_sold_categories' && (
                <div className={`${POPOVER_CHROME} tw-z-20`}>
                    <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-justify-start tw-items-center">
                        {renderBackButton('Co-sold Category')}
                    </div>
                    <div className="tw-self-stretch tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge">
                        {renderSearchInput('Search Co-sold Category')}
                    </div>
                    <div className="tw-self-stretch tw-max-h-64 tw-overflow-y-auto">
                        {filteredCategories.map((item) => (
                            <div
                                key={`coSoldCategories-${item.category_id}`}
                                className="tw-self-stretch tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-w-full"
                            >
                                <Checkbox
                                    size="sm"
                                    id={`co-sold-${item.category_id}`}
                                    data-testid="components-filterbutton-input-6"
                                    checked={isItemSelected(item, filters.co_sold_categories, 'category_id')}
                                    onChange={() => handleCoSoldCategorySelect(item)}
                                    label={item.category_name}
                                />
                            </div>
                        ))}
                        {filteredCategories.length === 0 && (
                            <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-justify-center tw-w-full">
                                <span className="tw-text-ink-secondary tw-text-sm tw-leading-tight">No results found</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeFilter === 'co_sold_category_types' && (
                <div className={`${POPOVER_CHROME} tw-z-20`}>
                    <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-justify-start tw-items-center">
                        {renderBackButton('Co-sold Category Type')}
                    </div>
                    <div className="tw-self-stretch tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge">
                        {renderSearchInput('Search Co-sold Category Type')}
                    </div>
                    <div className="tw-self-stretch tw-max-h-64 tw-overflow-y-auto">
                        {filteredCategoryTypes.map((item) => (
                            <div
                                key={`coSoldCategoryTypes-${item.category_type_id}`}
                                className="tw-self-stretch tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-w-full"
                            >
                                <Checkbox
                                    size="sm"
                                    id={`co-sold-type-${item.category_type_id}`}
                                    data-testid="components-filterbutton-input-7"
                                    checked={isItemSelected(item, filters.co_sold_category_types, 'category_type_id')}
                                    onChange={() => handleCoSoldCategoryTypeSelect(item)}
                                    label={item.category_type_name}
                                />
                            </div>
                        ))}
                        {filteredCategoryTypes.length === 0 && (
                            <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-justify-center tw-w-full">
                                <span className="tw-text-ink-secondary tw-text-sm tw-leading-tight">No results found</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeFilter === 'is_curated' && (
                <div className={`${POPOVER_CHROME} tw-z-20`}>
                    <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-justify-start tw-items-center">
                        {renderBackButton('Curated')}
                    </div>
                    <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-w-full">
                        <Checkbox
                            size="sm"
                            id="curated-active"
                            data-testid="components-filterbutton-input-8"
                            checked={!!filters.curatedSelections?.includes('active')}
                            onChange={() => handleCuratedCheckboxToggle('active')}
                            label="Active"
                        />
                    </div>
                    <div className="tw-self-stretch tw-h-9 tw-px-2.5 tw-py-2 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-flex tw-items-center tw-w-full">
                        <Checkbox
                            size="sm"
                            id="curated-inactive"
                            data-testid="components-filterbutton-input-9"
                            checked={!!filters.curatedSelections?.includes('inactive')}
                            onChange={() => handleCuratedCheckboxToggle('inactive')}
                            label="Inactive"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

FilterButton.displayName = 'FilterButton';

export default FilterButton;
